import { useState, useEffect } from "react";
import { Button } from "../../../Components/UI/button";
import { Input } from "../../../Components/UI/input";
import { Label } from "../../../Components/UI/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../Components/UI/select";
import { X, Check, ArrowLeft, Loader2, RefreshCw, Info } from "lucide-react";
import FieldWithIcon from "../../../Components/Common/FieldWithIcon";
import { getIntgnConfig, setIntgnConfig } from "../SubSettings/IntgnConfigAPI";
import { getAgencyData } from "../../../Utils/Storage";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { setGlobalLoading } from "../../../Store/Actions";
import { HoverCard,HoverCardContent, HoverCardTrigger } from "../../../Components/UI/hover-card";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "../../../Components/UI/tooltip";
import MobileInput from "../../../Components/Common/MobileInput";
import { countries } from "../../../Components/Enums/DataEnums";

const integrations = {
    indiaMart: { code: "IMRT", label: "IndiaMART", pushPath: "setIMLead" },
    tradeIndia: { code: "TI", label: "TradeIndia", pushPath: "setTILead" },
    justDial: { code: "JD", label: "JustDial", pushPath: "SetJDInquiry" },
    whatsapp: { code: "WA", label: "WhatsApp" },
    w3: { code: "WEB", label: "Website" }
};

const waDescription = {
  WA_KWSP: {
    isConfigured: "Your WhatsApp is configured. You can send a trial message below.",

    notConfigured: (
      <span className="cursor-auto leading-relaxed">
        <strong>Step 1:</strong> Go to{" "}
        <a
          href="https://k.api.io.in/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-color font-medium hover:underline"
        >
          API Dashboard
        </a>{" "}
        and log in.
        <br />
        <strong>Step 2:</strong> Navigate to the WhatsApp API page.
        <br />
        <strong>Step 3:</strong> Copy your <strong>Access Token</strong>.
      </span>
    )
  },

  WA_WASDR: {
    isConfigured: "WA Sender is already configured and ready to use.",
    notConfigured: "Configure your WA Sender credentials here."
  },

  WA_META: {
    isConfigured: "Meta WhatsApp Business account is connected successfully.",
    notConfigured: "Connect your Meta Business Account to enable WhatsApp messaging."
  }
};

const getWADescription = (wspProvider, isWhatsAppConfigured) => {
  const providerConfig = waDescription[wspProvider];

  if (!providerConfig) return null;

  return isWhatsAppConfigured[wspProvider]
    ? providerConfig.isConfigured
    : providerConfig.notConfigured;
};

// Field configurations for each integration
const getFieldConfigs = (intgrationType, pushUrl, isWhatsAppConfigured, wspProvider) => {
	const serviceProviderField = {
        id: "wspProvider",
        label: "Service Provider",
        type: "select",
        placeholder: "Select",
        options: [
            { value: "WA_KWSP", label: "K API" },
            // { value: "WA_WASDR", label: "WA Sender" },
            // { value: "WA_META", label: "Meta Official" } // Future proofing
        ],
        colSpan: "w-[120px] shrink-0" 
    };

	const providerFieldsMap = {
        WA_KWSP: [
            serviceProviderField,
            {
                id: "accessToken",
                label: "Access Token",
                type: "fieldWithIcon",
                placeholder: "e.g. 693a4729cXXXX",
                iconTypes: ["key"],
                colSpan: "flex-grow min-w-0",
                disabled: isWhatsAppConfigured["WA_KWSP"],
                readOnly: isWhatsAppConfigured["WA_KWSP"],
				required: true
            }
        ],
        WA_WASDR: [
			serviceProviderField,
            {
                id: "wasdrKey",
                label: "API Key",
                type: "fieldWithIcon",
                placeholder: "e.g. wasdr_key_123",
                iconTypes: ["key"],
                colSpan: "flex-grow min-w-0",
				disabled: isWhatsAppConfigured["WA_WASDR"],
				readOnly: isWhatsAppConfigured["WA_WASDR"],
				required: true
            },
            {
                id: "wasdrMobile",
                label: "Phone Number",
                type: "mobileInput",
                placeholder: "e.g. 9876543210",
                colSpan: "col-span-full w-full",
				disabled: isWhatsAppConfigured["WA_WASDR"],
				readOnly: isWhatsAppConfigured["WA_WASDR"],
                validation: "numeric",
                maxLength: 10
            }
        ],
        WA_META: [
			serviceProviderField,
            {
				id: "metaAccessToken",
				label: "Meta Access Token",
				type: "fieldWithIcon",
				placeholder: "e.g. EAAGm0PX4ZCpsBA...",
				iconTypes: ["key"],
				colSpan: "flex-grow min-w-0",
				disabled: isWhatsAppConfigured["WA_META"],
				readOnly: isWhatsAppConfigured["WA_META"],
				required: true    
		    }
        ]
    };

    // Safe fallback to WA_KWSP if provider is unknown
    const activeWAFields = providerFieldsMap[wspProvider] || providerFieldsMap["WA_KWSP"];

    const configs = {
        indiaMart: {
            sections: [
                {
                    title: "Manual Inquiry Import (PULL)",
                    showInfoIcon: true,
                    fields: [
                        {
                            id: "mobile",
                            label: "Mobile",
                            type: "mobileInput",
                            placeholder: "e.g. 8795487548",
                            colSpan: "flex-grow min-w-0",
                            validation: "numeric",
                            maxLength: 10
                        },
                        {
                            id: "version",
                            label: "Version",
                            type: "select",
                            options: [
                                { value: "v1", label: "V1" },
                                { value: "v2", label: "V2" }
                            ],
                            colSpan: "w-[72px] shrink-0"
                        },
                        {
                            id: "key",
                            label: "Key",
                            type: "fieldWithIcon",
                            placeholder: "e.g. 94e24217-28c9-4b2c-8e6c-abc123456789",
                            iconTypes: ["key"]
                        }
                    ],
                    description: "Enter your credentials to manually import inquiries from IndiaMart."
                },
                {
                    title: "Auto Inquiry Delivery (PUSH)",
                    description: "Make sure your IndiaMART plan supports real-time inquiry delivery. Share the link below with support to enable push integration.",
                    fields: [
                        {
                            id: "pushUrl",
                            type: "fieldWithIcon",
                            value: pushUrl,
                            iconTypes: ["copy"],
                            disabled: true,
                            readOnly: true,
                            className: "pt-1.5 cursor-pointer"
                        }
                    ]
                }
            ]
        },
        tradeIndia: {
            sections: [
                {
                    title: "Manual Inquiry Import (PULL)",
                    showInfoIcon: true,
                    fields: [
                        {
                            id: "userID",
                            label: "UserID",
                            type: "input",
                            placeholder: "e.g. 735473992278"
                        },
                        {
                            id: "profileID",
                            label: "ProfileID",
                            type: "input",
                            placeholder: "e.g. 68421579"
                        },
                        {
                            id: "key",
                            label: "Key",
                            type: "fieldWithIcon",
                            placeholder: "e.g. 91f3a7b2-6d4e-4c9f-96ab-3d1f812e6f2a",
                            iconTypes: ["key"]
                        }
                    ],
                    description: "Enter your credentials to manually import inquiries from TradeIndia."
                }
            ]
        },
        justDial: {
            sections: [
                {
                    title: "Auto Inquiry Delivery (PUSH)",
                    titleClassName: "font-medium text-lg text-foreground font-extrabold",
                    description: "JustDial can call our API whenever a new inquiry is received. Please share the following link with the JustDial Support team to enable this functionality.",
                    fields: [
                        {
                            id: "pushUrl",
                            type: "fieldWithIcon",
                            value: pushUrl,
                            iconTypes: ["copy"],
                            disabled: true,
                            className: "pt-3"
                        }
                    ]
                }
            ]
        },
        whatsapp: {
            sections: [
                {
                    title: "WhatsApp Configuration",
                    titleClassName: "font-medium text-lg text-foreground font-extrabold",
                    showInfoIcon: true,
                    description: getWADescription(wspProvider, isWhatsAppConfigured),
                    fields: activeWAFields
                }
            ]
        },
        w3: {
            sections: [
                {
                    title: "Auto Inquiry Delivery (PUSH)",
                    titleClassName: "font-medium text-lg text-foreground font-extrabold",
                    description: "You can integrate your website contact form with SahiClub to capture inquiries directly. Please use the key provided below and follow the instructions in the manual.",
                    fields: [
                        {
                            id: "website_key",
                            type: "fieldWithIcon",
                            iconTypes: ["key", "copy", "eye"],
                            forceNotAllowedCursor: true,
                            disabled: true,
                            className: "pt-3"
                        }
                    ]
                }
            ]
        }
    };
    return configs[intgrationType] || { sections: [] };
};

export default function IntgnConfig({ intgnName, isWebEdit = false, onClose }) {
    const config = integrations[intgnName] || {};
    const intgnType = config.code;
    const dispatch = useDispatch();

	const [isWhatsAppConfigured, setIsWhatsAppConfigured] = useState({ WA_KWSP: false, WA_WASDR: false, WA_META: false });
	const [countryCode, setCountryCode] = useState("91"); 
	const [wspProvider, setWspProvider] = useState("WA_KWSP");

    // Consolidated form data state
    const [formData, setFormData] = useState({
        mobile: "",
        version: "v2",
        key: "",
        pushUrl: "",
        userID: "",
        profileID: "",
        accessToken: "",
        website_key: "",
        whatsAppNumber: "",
        textMessage: "",
        fileInput:"",
    });

    // Store original data to compare changes
    const [originalData, setOriginalData] = useState({
        mobile: "",
        version: "v2",
        key: "",
        pushUrl: "",
        userID: "",
        profileID: "",
        accessToken: "",
        website_key: "",
        whatsAppNumber: "",
        textMessage: "",
        fileInput:"",
    });

    const [isLoading, setIsLoading] = useState(false);

    const { Mobile: adminMobile } = getAgencyData() || {};
    const { CC: adminCC } = getAgencyData() || {};
    const makePushUrl = path => {
        const baseUrl = intgnType === "JD" ? "https://sahicrm.com/Int.asmx" : "https://sahicrm.com/External.asmx";
        return `${baseUrl}/${path}?mob=${adminCC}_${adminMobile}`;
    };

    // Helper function to check if data has changed
    const hasDataChanged = () => {
        const fieldsToCompare = {
            IMRT: ['mobile', 'version', 'key'],
            TI: ['userID', 'profileID', 'key'],
            WA: ['accessToken','wasdrKey','wasdrMobile','metaAccessToken','metaBusinessID','metaPhoneID'], 
            JD: [],
            WEB: []
        };

        const relevantFields = fieldsToCompare[intgnType] || [];

        // If no fields to compare (JustDial, Website), return false
        if (relevantFields.length === 0) {
            return false;
        }

        // Compare only relevant fields for this integration type
        return relevantFields.some(field => {
            const currentValue = formData[field] || "";
            const originalValue = originalData[field] || "";
            return currentValue !== originalValue;
        });
    };

	const fetchConfig = async () => {
        try {
            dispatch(setGlobalLoading(true));

			const key = intgnType === "WA" ? "WD" : intgnType;
   			const integrationData = await getIntgnConfig(key);
            applyIntegrationConfig(integrationData);

        } catch (error) {
            console.error("Failed to fetch integration config:", error);
            toast.error("Failed to load integration configuration.");
        } finally {
            dispatch(setGlobalLoading(false));
        }
    };

    useEffect(() => {
        if (intgnType) {
            fetchConfig();
        }
    }, [intgnType, wspProvider]);

    const applyIntegrationConfig = async (integrationData) => {
        const baseFormData = {
            mobile: "",
            version: "v2",
            key: "",
            pushUrl: "",
            userID: "",
            profileID: "",
            accessToken: "",
			instanceID: "",
            website_key: "",
            whatsAppNumber: "",
            textMessage: "",
            fileInput:"",
			wspProvider: "WA_KWSP",
			WAMetaToken: "",
			WAWasdrKey: "",
			WAWasdrMobile: ""
        };

        let newFormData = { ...baseFormData };

        switch (intgnType) {
            case "IMRT":
                newFormData = {
                    ...baseFormData,
                    mobile: integrationData.IMNo || "",
                    version: integrationData.IMV || "v2",
                    key: integrationData.IMKey || "",
                    pushUrl: makePushUrl(config.pushPath)
                };
                break;

            case "TI":
                newFormData = {
                    ...baseFormData,
                    userID: integrationData.TIUID || "",
                    profileID: integrationData.TIPID || "",
                    key: integrationData.TIKey || "",
                    pushUrl: makePushUrl(config.pushPath)
                };
                break;

            case "JD":
                newFormData = {
                    ...baseFormData,
                    pushUrl: makePushUrl(config.pushPath)
                };
                break;

           case "WA": {
				const tokenObj =
					integrationData?.WDToken &&
					typeof integrationData.WDToken === "string"
						? (JSON.parse(integrationData.WDToken)?.[0] ?? {})
						: {};

				newFormData = {
					...baseFormData,
					wspProvider: "WA_KWSP",
					accessToken: tokenObj.accessToken || ""
				};
				break;
			}

            case "WEB":
                const apiKey = integrationData.APIKey || "";
                if (!apiKey) {
                    try {
                        const response = await setIntgnConfig("WEB", []);
                        const newKey = response?.data?.Table1?.[0]?.APIKey || "";

                        if (newKey) {
                            newFormData = {
                                ...baseFormData,
                                website_key: newKey
                            };
                        } else {
                            // Fallback: refetch
                            const updatedConfig = await getIntgnConfig("WEB");
                            const fallbackKey = updatedConfig?.Table1?.[0]?.APIKey || "";
                            newFormData = {
                                ...baseFormData,
                                website_key: fallbackKey
                            };
                        }
                    } catch (error) {
                        console.error("Web API key generation error:", error);
                        toast.error("Failed to generate Web API key");
                    }
                } else {
                    newFormData = {
                        ...baseFormData,
                        website_key: apiKey
                    };
                }
                break;

            default:
                newFormData = baseFormData;
                break;
        }

        setFormData(newFormData);
        setOriginalData(newFormData); // Store original data for comparison
		if(intgnType === "WA" && newFormData.instanceID)
		{
			setIsWhatsAppConfigured(true);
		}else{
			setIsWhatsAppConfigured(false);
		}
    };

    const validateForm = () => {
        switch (intgnType) {
            case "WA":
                 if (!formData.accessToken) {
                        toast.error("Please enter a valid Instance Token.");
                        return false;
                    }
                break;

            case "IMRT":
                // Mobile validation
                if (!formData.mobile) {
                    toast.error("Please enter a valid mobile number.");
                    return false;
                } else if (formData.mobile.length !== 10) {
                    toast.error("Mobile number must be exactly 10 digits.");
                    return false;
                } else if (!/^\d{10}$/.test(formData.mobile)) {
                    toast.error("Mobile number must contain only digits");
                    return false;
                }

                // Key validation
                if (!formData.key || formData.key.trim() === "") {
                    toast.error("Please enter a valid key.");
                    return false;
                }
                break;

            case "TI":
                // UserID validation
                if (!formData.userID || formData.userID.trim() === "") {
                    toast.error("Please enter a valid UserID.");
                    return false;
                }

                // ProfileID validation
                if (!formData.profileID || formData.profileID.trim() === "") {
                    toast.error("Please enter a valid ProfileID.");
                    return false;
                }

                // Key validation
                if (!formData.key || formData.key.trim() === "") {
                    toast.error("Please enter a valid key.");
                    return false;
                }
                break;
			default:
				break;
        }
        return true;
    };

    const buildPayload = () => {
        switch (intgnType) {
            case "WA":
				if (wspProvider === "WA_WASDR") {
					return {
						changeType: "WD",
						key: "WASDR",
						extraValues: [{
							wasdrKey: formData.wasdrKey,
							wasdrMobile: formData.wasdrMobile
						}]
					};
				} else if (wspProvider === "WA_META") {
					return {
						changeType: "WD",
						key: "META",
						extraValues: [{
							metaAccessToken: formData.metaAccessToken,
							metaBusinessID: formData.metaBusinessID,
							metaPhoneID: formData.metaPhoneID
						}]
					};
				} else if (wspProvider === "WA_KWSP") {
					return {
						changeType: "WD",
						key: "KWSP",
						extraValues: [{
							accessToken: formData.accessToken
						}]
					};
				}
				break;

            case "IMRT":
                return {
                    changeType: "IMRT",
                    extraValues: [{
                        imMobile: formData.mobile,
                        imKey: formData.key,
                        imversion: formData.version
                    }]
                };

            case "TI":
                return {
                    changeType: "TI",
                    extraValues: [{
                        tiUserID: formData.userID,
                        tiProfileID: formData.profileID,
                        tiKey: formData.key
                    }]
                };
            default:
                return { changeType: intgnType, extraValues: [] };
        }
    };

    const handleSave = async () => {
        // Check if data has changed
        if (!hasDataChanged()) {
            toast.info("No changes were made to update.");
            return;
        }

        // Validate form
        if (!validateForm()) {
            return;
        }

        dispatch(setGlobalLoading(true));
        setIsLoading(true);
        try {
			
            const { key, changeType, extraValues } = buildPayload();
            const response = await setIntgnConfig(key, changeType, extraValues);
			
            if (response.error === false) {
                toast.success("Integration configuration updated.");
				onClose();
            }
        } catch (error) {
            toast.error(error.message || ". Failed to update integration configuration");
            console.error('Error saving profile:', error);
        } finally {
            setIsLoading(false);
            dispatch(setGlobalLoading(false));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (fieldName, value) => {

		if(fieldName === "wspProvider"){
			setWspProvider(value);
		}
        setFormData(prev => ({ ...prev, [fieldName]: value }));
    };

    // Field rendering function
    const renderFormField = (field) => {
        const { id, label, type, placeholder, options, disabled, readOnly, className, iconTypes, validation, value, forceNotAllowedCursor, maxLength, required } = field;
        const fieldValue = value !== undefined ? value : formData[id] || "";

        switch (type) {
            case "input":
				if (intgnType === "WA") {
                    return (
                        <div key={id} className="space-y-2">
                            <div className="flex gap-2 items-center">
								<Label htmlFor={id}>{label}</Label>
							</div>
                            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                                <div className="flex-1">
                                    <Input
										id={id}
										name={id}
										value={fieldValue}
										onChange={handleInputChange}
										placeholder={placeholder}
										disabled={disabled}
										readOnly={readOnly}
										className={className}
									/>
                                </div>
								<Button
									className="sahi-primary-button w-full sm:w-auto"
									onClick={handleSave}
								>
									<div className="flex items-center gap-2">
										<Check className="h-3 w-3" />
										<span>Send</span>
									</div>
								</Button>
                            </div>
                        </div>
                    );
                }

                return (
                    <div key={id} className="space-y-1.5">
                        <Label htmlFor={id}>{label}</Label>
                        <Input
                            id={id}
                            name={id}
                            value={fieldValue}
                            onChange={handleInputChange}
                            placeholder={placeholder}
                            disabled={disabled}
                            readOnly={readOnly}
                            className={className}
                        />
                    </div>
                );

            case "mobileInput":
                return (
                    <div key={id} className="space-y-1.5">
                        <Label htmlFor={id}>{label}</Label>
                        <MobileInput
                            id={id}
                            name={id}
                            pattern="[0-9]{10}"
                            value={fieldValue}
                            placeholder={placeholder}
                            maxLength={maxLength || 10}
                            onChange={e => {
                                const v = e.target.value;   
                                if (/^\d{0,10}$/.test(v)) {
                                    setFormData(prev => ({ ...prev, [id]: v }));
                                }
                            }}
                            onKeyPress={e => {
                                if (!/[0-9]/.test(e.key)) {
                                    e.preventDefault();
                                }
                            }}
							selectedCountryCode={countryCode}
							onCountryCodeChange={(newCountryCode) => {
								setCountryCode(newCountryCode);

								const newCountry = countries.find(c => c.CC === newCountryCode);
								if (newCountry) {
									handleInputChange({
									target: {
										name: 'country',
										value: newCountry.Country
									}
									});

									if (newCountry.Country !== "India") {
									handleInputChange({
										target: {
										name: 'state',
										value: ""
										}
									});
									}
								}
								}}
                            disabled={disabled}
                            className={className}
                        />
                    </div>
                );

            case "select":
				const selectedOption = options?.find(opt => opt.value === fieldValue);

                return (
                    <div key={id} className="space-y-1.5">
                        <Label htmlFor={id}>{label}</Label>
                        <Select
                            value={fieldValue}
                            onValueChange={v => handleSelectChange(id, v)}
                            disabled={disabled}
                        >
                            <SelectTrigger id={id} className={className}>
                                <span className={fieldValue ? "text-foreground" : "text-gray-500"}>
                                    {selectedOption ? selectedOption.label : placeholder}
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                {options?.map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                );

            case "fieldWithIcon":
                if (intgnType === "WA") {
                    return (
						<div key={id} className="space-y-2">
							<div className="flex gap-2 items-center">
								<Label htmlFor={id}>{label}</Label>
							</div>
							<div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
								<div className="flex-1">
									<FieldWithIcon
										fieldID={id}
										label=""
										placeholder={placeholder}
										value={fieldValue}
										iconTypes={iconTypes || []}
										onChange={disabled ? undefined : handleInputChange}
										disabled={disabled}
										readOnly={readOnly}
										className={className}
										forceNotAllowedCursor={forceNotAllowedCursor}
									/>
								</div>						
							</div>
						</div>
                    );
                }
                
                return (
                    <FieldWithIcon
                        key={id}
                        fieldID={id}
                        label={label}
                        placeholder={placeholder}
                        value={fieldValue}
                        iconTypes={iconTypes || []}
                        onChange={disabled ? undefined : handleInputChange}
                        disabled={disabled}
                        readOnly={readOnly}
                        className={className}
                        forceNotAllowedCursor={forceNotAllowedCursor}
                    />
                );

            default:
                return null;
        }
    };

    // Get field configuration based on integration type
    const fieldConfig = getFieldConfigs(intgnName, formData.pushUrl || makePushUrl(config.pushPath), isWhatsAppConfigured, wspProvider);

    const renderBody = () => {
        if (!fieldConfig.sections.length) return null;

        return (
            <div className="space-y-6">
                {fieldConfig.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex}>
                        {/* Section Header */}
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`text-sm text-foreground font-extrabold ${section.titleClassName || ""}`}>
                                    {section.title}
                                </span>
                                {section.showInfoIcon && (
                                    <HoverCard openDelay={200} closeDelay={100}>
                                        <HoverCardTrigger asChild>
                                            <button
                                                type="button"
                                                className="inline-flex items-center justify-center"
                                            >
                                                <span className="info-icon">i</span>
                                                <span className="sr-only">Information</span>
                                            </button>
                                        </HoverCardTrigger>
										<HoverCardContent
												side={intgnType === "WA" ? "bottom" : "top"}
												align="start"
												sideOffset={4}
												className={`${intgnType === "WA" ? "w-80" : "w-64"} p-3 bg-white border border-gray-200 text-gray-900`}
											>
                                            <div className="space-y-2">
                                                <p className="text-sm text-gray-600 ">
                                                    {section.description}
                                                </p>
                                            </div>
                                        </HoverCardContent>
                                    </HoverCard>
                                )}
                            </div>
                            {section.description && (
                                <p className="text-xs text-muted-foreground mb-4">
                                    {section.description}
                                </p>
                            )}

                            {/* Fields Container */}
                            {section.fields.length > 0 && (
                                <div className="space-y-4">
                                    {/* Special handling for IndiaMART mobile/version row */}
                                    {intgnName === "indiaMart" && sectionIndex === 0 && (
                                        <div className="flex gap-4 mb-4 flex-nowrap">
                                            {section.fields.slice(0, 2).map(field => (
                                                <div key={field.id} className={field.colSpan}>
                                                    {renderFormField(field)}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Regular fields rendering */}
                                    {section.fields.slice(intgnName === "indiaMart" && sectionIndex === 0 ? 2 : 0).map(field => (
                                        renderFormField(field)
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderFooter = () => {
        const hideUpdateBtn = config.label === "JustDial" || config.label === "Website";
        return (
            <div className="py-4 sm:sticky sm:bottom-0 sm:bg-background sm:pb-3">
                <div className="flex gap-4 sm:flex-row sm:justify-start sm:gap-3 w-full">
                    {!hideUpdateBtn && (
                        <Button
                            onClick={handleSave}
                            className="sahi-primary-button"
                            disabled={!hasDataChanged() || isLoading}
                        >
                            <Check className="h-4 w-4" />
                            {isLoading ? "Updating..." : "Update Configuration"}
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        className="sm:flex-none sm:min-w-[100px]"
                    >
                        <X className="w-3 h-3" />
                        Close
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <Dialog open onOpenChange={onClose} preventOutsideClick>
            <DialogContent className="sm:max-w-[540px] w-full">
                <DialogHeader >
                    {/* Mobile View */}
                    <div className="sm:hidden flex items-center justify-between w-full h-8">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <DialogClose className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                <ArrowLeft className="h-6 w-6" />
                                <span className="sr-only">Back</span>
                            </DialogClose>
                            <DialogTitle className="text-lg font-medium">
                                <div className="truncate">
                                    Configuration{isWebEdit ? " (Web)" : ""} – {config.label}
                                </div>
                            </DialogTitle>
                        </div>
                    </div>

                    {/* Desktop Title */}
                    <DialogTitle className="hidden sm:block text-lg md:text-xl">
                        Configuration{isWebEdit ? " (Web)" : ""} – {config.label}
                    </DialogTitle>

                    {/* Desktop Right Side */}
                    <div className="hidden sm:flex items-center gap-2">
                        <DialogClose className="flex-shrink-0 p-1 sm:text-text-color sm:hover:bg-gray-200 rounded-md transition-colors">
                            <X className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </DialogClose>
                    </div>
                </DialogHeader>
                <div className="px-4 flex-1 scrollbar-no-arrows overflow-y-scroll sm:max-h-[85vh] sm:pb-0">
                    {isLoading && !formData.mobile && !formData.userID && !formData.accessToken && !formData.website_key ? (
                        <div className="flex justify-center items-center py-8">
                            <p className="text-muted-foreground">Loading configuration...</p>
                        </div>
                    ) : (
                        <>
                            {renderBody()}
                            {renderFooter()}
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}